import { useRouter } from "next/router";
import { useCallback } from "react";

import { ORDER_TYPE_ENUM, QUERY_PARAM_ENUM } from "@/constants/enums";
import { useAppDispatch, useAppSelector } from "@/store";
import { setOrderType, setTable } from "@/store/slices/cartSlice";
import { resumeDineinTable } from "@/store/thunks/dinein";
import type { RestaurantTable } from "@/types";

/**
 * `uj` — the one place a dine-in table is committed to the cart.
 *
 * Both callers go through it: the table picker (`DINE_IN_SEARCH_TABLES_MODAL`)
 * and the QR deep link on `/menu/dine-in`. It does three things, all of them the
 * original's:
 *
 * 1. **switches the cart to dine-in** — picking a table IS choosing the order
 *    type, which is why there is no dine-in card on the landing page;
 * 2. **keeps whatever the customer already set.** The guest count, in
 *    particular, survives a change of table: `num_of_guests: prev?.num_of_guests
 *    || table.num_of_guests || 1` reads the cart first, the table's current
 *    sitting second, and only then falls back to one cover;
 * 3. **writes `?floor_id=&table_id=` onto the current URL** with
 *    `history.replaceState` rather than a router navigation, so the address bar
 *    matches the QR code the table carries and a refresh — or a share — lands on
 *    the same table. Passing no table clears the query string.
 *
 * `replaceState` and not `router.replace` is deliberate and is the original's
 * own call: a Next navigation here would re-run the page's data effects and
 * re-fetch the catalog on every table change.
 *
 * 4. **asks whether the table already has a bill** — the original's
 *    `setUpdatePreselectTableCartInfoOnInit` ends with
 *    `u(Y({ tableId: _, replace: null == d }))`, the resume lookup. This is the
 *    first of its two call sites (the second is the checkout's own mount), and
 *    without it every round at a table opened a NEW order while the earlier one
 *    stayed open and uncollectable.
 */
export default function useSelectDineinTable() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const current = useAppSelector((state) => state.cart.table);
  const tenantId = useAppSelector((state) => state.settings.data._id);

  return useCallback(
    (table?: RestaurantTable | null) => {
      dispatch(setOrderType(ORDER_TYPE_ENUM.DINE_IN));
      dispatch(
        table
          ? setTable({
              tableId: table._id,
              tableName: table.name,
              floorId: table.floor_id ?? null,
              guests: current?.guests || table.num_of_guests || 1,
            })
          : setTable(null),
      );

      // Does this table already have a bill running? `setTable` has just
      // released any previously adopted one, so a table with no open order
      // correctly leaves the cart posting a NEW order.
      if (table?._id) {
        void dispatch(resumeDineinTable({ tableId: table._id, tenantId }));
      }

      if (typeof window === "undefined") return;
      const floorId = table?.floor_id;
      const tableId = table?._id;
      const query =
        floorId && tableId
          ? `?${new URLSearchParams({
              [QUERY_PARAM_ENUM.floorId]: floorId.trim(),
              [QUERY_PARAM_ENUM.tableId]: tableId.trim(),
            }).toString()}`
          : "";
      const url = `${router.pathname}${query}`;
      window.history.replaceState(
        { ...window.history.state, as: url, url },
        "",
        url,
      );
    },
    [current?.guests, dispatch, router.pathname, tenantId],
  );
}
